import { useMemo, useContext } from 'react'
import { flexRender } from '@tanstack/react-table'
import { Table } from 'reactstrap'
import Select from 'react-select'
import { TanstackContext } from '../TanstackContext'
import TanstackPaginator from '../TanstackPaginator'

const TableRows = () => {
  const { table, sorting } = useContext(TanstackContext)

  const {
    getAllColumns,
    getAllFlatColumns,
    getAllLeafColumns,
    getCanNextPage,
    getCanPreviousPage,
    getCanSomeRowsExpand,
    getCenterFlatHeaders,
    getCenterFooterGroups,
    getCenterHeaderGroups,
    getCenterLeafColumns,
    getCenterLeafHeaders,
    getCenterTotalSize,
    getCenterVisibleLeafColumns,
    getColumn,
    getCoreRowModel: getCoreRowModelFunc,
    getExpandedDepth,
    getExpandedRowModel,
    getFilteredRowModel: getFilteredRowModelFunc,
    getFilteredSelectedRowModel,
    getFlatHeaders,
    getFooterGroups,
    getGlobalAutoFilterFn,
    getGlobalFacetedMinMaxValues,
    getGlobalFacetedRowModel,
    getGlobalFacetedUniqueValues,
    getGlobalFilterFn,
    getGroupedRowModel,
    getGroupedSelectedRowModel,
    getHeaderGroups,
    getIsAllColumnsVisible,
    getIsAllPageRowsSelected,
    getIsAllRowsExpanded,
    getIsAllRowsSelected,
    getIsSomeColumnsPinned,
    getIsSomeColumnsVisible,
    getIsSomePageRowsSelected,
    getIsSomeRowsExpanded,
    getIsSomeRowsSelected,
    getLeafHeaders,
    getLeftFlatHeaders,
    getLeftFooterGroups,
    getLeftHeaderGroups,
    getLeftLeafColumns,
    getLeftLeafHeaders,
    getLeftTotalSize,
    getLeftVisibleLeafColumns,
    getPageCount,
    getPageOptions,
    getPaginationRowModel: getPaginationRowModelFunc,
    getPreExpandedRowModel,
    getPreFilteredRowModel,
    getPreGroupedRowModel,
    getPrePaginationRowModel,
    getPreSelectedRowModel,
    getPreSortedRowModel,
    getRightFlatHeaders,
    getRightFooterGroups,
    getRightHeaderGroups,
    getRightLeafColumns,
    getRightLeafHeaders,
    getRightTotalSize,
    getRightVisibleLeafColumns,
    getRow,
    getRowModel,
    getSelectedRowModel,
    getSortedRowModel: getSortedRowModelFunc,
    getState,
    getToggleAllColumnsVisibilityHandler,
    getToggleAllPageRowsSelectedHandler,
    getToggleAllRowsExpandedHandler,
    getToggleAllRowsSelectedHandler,
    getTotalSize,
    getVisibleFlatColumns,
    getVisibleLeafColumns,
    initialState,
    nextPage,
    options,
    previousPage,
    reset,
    resetColumnFilters,
    resetColumnOrder,
    resetColumnPinning,
    resetColumnSizing,
    resetColumnVisibility,
    resetExpanded,
    resetGlobalFilter,
    resetGrouping,
    resetHeaderSizeInfo,
    resetPageIndex,
    resetPageSize,
    resetPagination,
    resetRowSelection,
    resetSorting,
    setColumnFilters,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setColumnSizingInfo,
    setColumnVisibility,
    setExpanded,
    setGlobalFilter,
    setGrouping,
    setOptions,
    setPageCount,
    setPageIndex,
    setPageSize,
    setPagination,
    setRowSelection,
    setSorting,
    setState,
    toggleAllColumnsVisible,
    toggleAllPageRowsSelected,
    toggleAllRowsExpanded,
    toggleAllRowsSelected,
  } = table

  const getTableRows = (row) => {
    const rowsCells = row.getVisibleCells()
    return rowsCells.map((rowCell) => {
      const { id } = rowCell
      return (
        <tr key={id}>
          <td>{flexRender(rowCell.column.columnDef.header, rowCell.getContext())}</td>
          <td>{flexRender(rowCell.column.columnDef.cell, rowCell.getContext())}</td>
        </tr>
      )
    })
  }

  const tables = useMemo(() => {
    const { rows } = getRowModel()
    return rows.map((row) => {
      const { id } = row
      const tbRows = getTableRows(row)
      return (
        <Table bordered hover striped key={id}>
          <tbody>{tbRows}</tbody>
        </Table>
      )
    })
  }, [table, sorting])

  return (
    <div>
      <SortSelect />
      <div className="d-flex flex-column gap-3">
        {tables}
        <TanstackPaginator />
      </div>
    </div>
  )
}

const SortIconButton = () => {
  const { table, sorting } = useContext(TanstackContext)

  if (sorting.length <= 0) return null

  const { id, desc } = sorting[0]

  const toggleFunc = () => {
    table.setSorting([{ id, desc: !desc }])
  }

  const sortDesc = 'bi bi-sort-down-alt h1'
  const sortAsc = 'bi bi-sort-up-alt h1'
  const className = desc ? sortDesc : sortAsc
  return <i className={className} role="button" onClick={() => toggleFunc()} tabIndex={0} />
}

const SortSelect = () => {
  const defaultOption = { label: 'Default', value: '' }
  const { table, sorting } = useContext(TanstackContext)

  const options = useMemo(() => {
    return [
      defaultOption,
      ...table
        .getAllColumns()
        .filter((c) => c.getCanSort())
        .map((c) => {
          const { id, columnDef } = c
          return { label: columnDef.header, value: id }
        }),
    ]
  }, [table])

  const value = useMemo(() => {
    if (sorting.length <= 0) return defaultOption
    const op = sorting[0]
    return options.find((x) => x.value === op.id)
  }, [options, sorting])

  return (
    <div className="d-flex gap-3 mb-3">
      <div className="flex-grow-1">
        <Select
          options={options}
          onChange={(option) => {
            table.setSorting([{ id: option.value, desc: true }])
          }}
          value={value}
        />
      </div>
      <div className="d-flex align-items-center">
        <SortIconButton />
      </div>
    </div>
  )
}

export default TableRows
